import { useEffect, useState } from 'react';
import { fhirClient } from 'src/App';
import { apiClient } from 'src/careflow/ph-core/api_client/api_client';
import { Bundle, Patient } from 'src/careflow/ph-core/fhir-client/resources';
import { getFhirIdFromEntity } from 'src/utils/fhir';

type PatientSummary = { scope: string; name: string; count: number; icon: string; type?: string };

const usePatientSummary = (patientId: string | null, translate: any) => {
  const [data, setData] = useState<PatientSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<any>(null);

  useEffect(() => {
    if (!patientId) return;

    let active = true;

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const [workflowRes, patientRes, serviceRequestRes, taskRes, episodeRes] = await Promise.all(
          [
            apiClient.get(`/core/workflow/box/count/${patientId}`),
            fhirClient.getResource<Patient>(`Patient/${patientId}`),
            fhirClient.search<Bundle>(
              `ServiceRequest?patient=${patientId}&status=draft,active,on-hold,unknown`
            ),
            fhirClient.search<Bundle>(
              `Task?patient=${patientId}&status=draft,requested,received,accepted,ready,in-progress,on-hold`
            ),
            fhirClient.search<Bundle>(
              `EpisodeOfCare?patient=${patientId}&status=planned,waitlist,active,onhold`
            ),
          ]
        );

        const gpIds =
          patientRes?.generalPractitioner
            ?.map((e) => getFhirIdFromEntity(e?.reference ?? ''))
            ?.filter(Boolean) ?? [];

        let gpCount = 0;

        if (gpIds.length) {
          const gpRes = await fhirClient.search<Bundle>(
            `PractitionerRole?_id=${gpIds.join(',')}&active=true`
          );
          gpCount = gpRes?.total ?? 0;
        }

        if (!active) return;

        let workflowCount: any = workflowRes ?? [];

        const data = [
          ...workflowCount,
          {
            scope: 'serviceRequest',
            name: translate('details.patient.transfer.serviceRequest'),
            count: serviceRequestRes?.total ?? 0,
            icon: 'grommet-icons:resources',
          },
          {
            scope: 'task',
            name: translate('details.patient.transfer.task'),
            count: taskRes?.total ?? 0,
            icon: 'grommet-icons:resources',
          },
          {
            scope: 'episodeOfCare',
            name: translate('details.patient.transfer.episodeOfCare'),
            count: episodeRes?.total ?? 0,
            icon: 'grommet-icons:resources',
          },
          {
            scope: 'generalPractitioner',
            name: translate('details.patient.transfer.generalPractitioners'),
            count: gpCount,
            icon: 'grommet-icons:resources',
          },
        ];

        setData(data);
      } catch (err) {
        if (!active) return;
        setError(err);
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchData();

    return () => {
      active = false;
    };
  }, [patientId]);

  return { data, loading, error };
};

export default usePatientSummary;
